import { useCallback, useEffect, useRef } from 'react';
import type { PointerEventHandler, RefObject } from 'react';
import { BLAST_AT_S, BOMB, CANVAS, CITY_MASK, FUSE_MS, LAYERS } from './bgData';
import { armGrenade, fireGrenade, stopGrenade } from './grenadeAudio';

/* ---------------------------------------------------------------- feel ---- */
/* Everything here is canvas pixels and seconds. Turn GRAVITY for how heavy it
   is, BOUNCE for how lively a roof is, and MAX_THROW for how hard an arm can
   get. They are independent: BOUNCE does not change how far a throw goes. */

/** Canvas px/s². The city is 1920 wide, so this is "heavy toy", not "moon". */
const GRAVITY = 2400;
/** Fraction of the approach speed a surface gives back. */
const BOUNCE = 0.42;
/** Fraction of the sideways speed kept per hit — the rest becomes spin. */
const FRICTION = 0.72;
/** Velocity lost to the air, per second. */
const AIR = 0.14;
/** However hard you flick, this is the arm's limit. */
const MAX_THROW = 2800;
/** Below this it has stopped, and stays stopped until someone picks it up. */
const SLEEP_V = 18;
/** Physics step. 2800 px/s moves 12px a step, a third of the ball — nothing
 *  thin enough to tunnel through is in the mask. */
const STEP = 1 / 240;
/** Contact probes around the ball. */
const PROBES = 24;
/** How much of the end of a drag the throw is measured over. The very last
 *  pointer event is usually a 1px twitch and would throw it nowhere. */
const THROW_WINDOW_MS = 90;
/** Tumble off a throw, as a divisor of speed/radius. A thrown thing is not a
 *  rolling thing, so this is by eye: 12 is about one turn per hardest throw. */
const TUMBLE = 12;
/** Rolling spin is vt / r, which at full speed is 8 turns a second and reads as
 *  a strobe rather than as a roll. rad/s. */
const MAX_SPIN = 30;

/* --------------------------------------------------------------- blast ---- */
/** Half the explosion canvas. Everything it draws stays inside this. */
const BLAST_R = 200;
/** The flash and the ring. */
const BLAST_MS = 620;
/** Debris outlives the flash. */
const DEBRIS_MS = 1150;
const DEBRIS = 30;
/** When the replacement starts climbing — while the last sparks are still out. */
const RISE_AT_MS = 620;
/** Matches --bomb-rise in home.css; both read the same figure. */
const RISE_MS = 620;
/**
 * What is left of the fuse when the church is lit again.
 *
 * The choir starts at that instant rather than after a pause, so the fuse left
 * to burn is exactly the clip's own run-up to the bang. A relit grenade is
 * therefore a shade quicker than a fresh one, and it announces itself.
 */
const RELIGHT_MS = BLAST_AT_S * 1000;
const SHAKE_MS = 420;
const SHAKE_PX = 7;

/** Flat, in the city's own palette: white-hot, bulb, window. */
const HOT: [number, number, number][] = [
  [255, 246, 214],
  [255, 215, 122],
  [223, 126, 26],
];

/** Where the ball's centre sits when the dome is on the church. */
const DOCK_X = BOMB.x + BOMB.cx;
const DOCK_Y = BOMB.y + BOMB.cy;

/** The church, as a box. */
const SOCKET = LAYERS.find((l) => l.src === BOMB.behind);

/**
 * Is the ball completely out of the church?
 *
 * Until it is, the dome stays where it lives in the layer stack — *behind* the
 * church — so pulling it up is the ball rising out of its socket, past the
 * crenellations and the lit arches, rather than a ball that snaps in front of
 * the roof the instant it moves.
 */
function clearOfSocket(x: number, y: number): boolean {
  if (!SOCKET) return true;
  return (
    x + BOMB.r <= SOCKET.x ||
    x - BOMB.r >= SOCKET.x + SOCKET.w ||
    y + BOMB.r <= SOCKET.y ||
    y - BOMB.r >= SOCKET.y + SOCKET.h
  );
}

type Phase = 'docked' | 'held' | 'loose' | 'blast' | 'rising';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  colour: number;
}

export interface GrenadeHandle {
  /** The dome sprite. Mount it where BOMB.src sits in LAYERS — *behind* the
   *  church, which is what lets the replacement climb out from inside it. */
  spriteRef: RefObject<HTMLImageElement>;
  /** The explosion. Mount it last, over everything. */
  blastRef: RefObject<HTMLCanvasElement>;
  /** The grab target, which follows the grenade. Mount it LAST in the hit SVG
   *  so it wins a press over whatever building it happens to be in front of. */
  hitRef: RefObject<SVGCircleElement>;
  handlers: {
    onPointerDown: PointerEventHandler<SVGGElement>;
    onPointerMove: PointerEventHandler<SVGGElement>;
    onPointerUp: PointerEventHandler<SVGGElement>;
    onPointerCancel: PointerEventHandler<SVGGElement>;
  };
}

/**
 * The dome of Hram Svetog Save is a Holy Hand Grenade. Pull it off, throw it,
 * and three seconds after you first let go it goes off wherever it happens to
 * be — in the air, on a roof, or still in your hand.
 *
 * Nothing here goes through React state. A throw is 60 positions a second and
 * a contact test per 4ms; re-rendering the skyline for that would be work with
 * nothing to show for it, so the sprite is moved by writing its transform and
 * the blast is painted straight onto its own canvas, exactly as the harp's
 * flash and Kulčina's display are.
 *
 * @param shakeRef the element to shake when it goes off — the city band.
 * @param lit      whether Hram Svetog Save's own windows are burning. They are
 *                 the fuse's power: dark, nothing goes off.
 */
export function useGrenade(shakeRef: RefObject<HTMLElement | null>, lit: boolean): GrenadeHandle {
  const spriteRef = useRef<HTMLImageElement>(null);
  const blastRef = useRef<HTMLCanvasElement>(null);
  const hitRef = useRef<SVGCircleElement>(null);

  /** Read at fuse time, so toggling the lights never rebuilds the handlers. */
  const litRef = useRef(lit);
  /** The collision silhouette: one byte per pixel of the city band. */
  const mask = useRef<Uint8Array | null>(null);
  const image = useRef<ImageData | null>(null);
  const bits = useRef<Particle[]>([]);
  const raf = useRef(0);
  const clock = useRef(0);
  const timers = useRef<number[]>([]);
  /** The tail of the drag, for the throw. */
  const trail = useRef<{ t: number; x: number; y: number }[]>([]);

  const st = useRef({
    phase: 'docked' as Phase,
    x: DOCK_X,
    y: DOCK_Y,
    vx: 0,
    vy: 0,
    spin: 0,
    av: 0,
    grabX: 0,
    grabY: 0,
    pointer: -1,
    /** Pin pulled: it has been let go at least once. Stays true in the dark. */
    armed: false,
    /** When the bang is due, or 0 — which, armed, means the church is dark. */
    fuseAt: 0,
    asleep: false,
    /** Lifted out of the layer stack, so it passes in front of the city. */
    free: false,
    /** Let go inside something — fall out of it rather than be spat out of it. */
    ghost: false,
    blastAt: 0,
    shakeAt: 0,
    bx: 0,
    by: 0,
  });

  /* ------------------------------------------------------------- mask ---- */
  // Read once into a byte per pixel. The silhouette rides in ALPHA for the same
  // reason the harp's index map does: no colour profile can rewrite it on the
  // way through a canvas.
  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const c = document.createElement('canvas');
      c.width = CITY_MASK.w;
      c.height = CITY_MASK.h;
      const g = c.getContext('2d', { willReadFrequently: true });
      if (!g) return;
      g.drawImage(img, 0, 0);
      const d = g.getImageData(0, 0, CITY_MASK.w, CITY_MASK.h).data;
      const bytes = new Uint8Array(CITY_MASK.w * CITY_MASK.h);
      for (let p = 3, o = 0; p < d.length; p += 4, o++) bytes[o] = (d[p] ?? 0) > 0 ? 1 : 0;
      mask.current = bytes;
    };
    img.src = CITY_MASK.src;
    return () => {
      cancelled = true;
    };
  }, []);

  const solid = useCallback((px: number, py: number): boolean => {
    const m = mask.current;
    if (!m) return false;
    const my = (py - CITY_MASK.y) | 0;
    if (my < 0) return false; // sky
    if (my >= CITY_MASK.h) return true; // under the ground line, which is floor
    const mx = px | 0;
    if (mx < 0 || mx >= CITY_MASK.w) return false; // the walls below handle these
    return m[my * CITY_MASK.w + mx] === 1;
  }, []);

  /* ---------------------------------------------------------- physics ---- */
  const touching = useCallback(
    (x: number, y: number): boolean => {
      for (let k = 0; k < PROBES; k++) {
        const a = (k / PROBES) * Math.PI * 2;
        if (solid(x + Math.cos(a) * BOMB.r, y + Math.sin(a) * BOMB.r)) return true;
      }
      return false;
    },
    [solid],
  );

  const contact = useCallback(() => {
    const s = st.current;
    let nx = 0;
    let ny = 0;
    let hits = 0;
    for (let k = 0; k < PROBES; k++) {
      const a = (k / PROBES) * Math.PI * 2;
      const dx = Math.cos(a);
      const dy = Math.sin(a);
      if (solid(s.x + dx * BOMB.r, s.y + dy * BOMB.r)) {
        nx -= dx;
        ny -= dy;
        hits += 1;
      }
    }
    if (!hits) return;
    // The normal is where the wall *isn't*, averaged. Buried to the middle
    // (dropped inside a tower) that sums to nothing, and up is as good as any.
    let len = Math.hypot(nx, ny);
    if (len < 1e-4) {
      nx = 0;
      ny = -1;
      len = 1;
    }
    nx /= len;
    ny /= len;
    // Out a pixel at a time: the mask is the surface, and there is no analytic
    // depth to solve for. Bounded, because each step only ever went 12px in.
    for (let k = 0; k < 14 && touching(s.x, s.y); k++) {
      s.x += nx;
      s.y += ny;
    }
    const vn = s.vx * nx + s.vy * ny;
    if (vn < 0) {
      s.vx -= (1 + BOUNCE) * vn * nx;
      s.vy -= (1 + BOUNCE) * vn * ny;
    }
    const tx = -ny;
    const ty = nx;
    const was = s.vx * tx + s.vy * ty;
    const vt = was * FRICTION;
    s.vx += (vt - was) * tx;
    s.vy += (vt - was) * ty;
    // What it keeps sideways it keeps as spin: rolling, not skidding — which
    // for a ball of this radius is exactly vt / r.
    s.av = Math.max(-MAX_SPIN, Math.min(MAX_SPIN, vt / BOMB.r));
    if (Math.hypot(s.vx, s.vy) < SLEEP_V) {
      s.vx = 0;
      s.vy = 0;
      s.av = 0;
      s.asleep = true;
    }
  }, [solid, touching]);

  const integrate = useCallback(
    (dt: number) => {
      const s = st.current;
      if (s.asleep) return;
      let left = Math.min(dt, 0.05);
      while (left > 0) {
        const h = Math.min(STEP, left);
        left -= h;
        s.vy += GRAVITY * h;
        const drag = Math.exp(-AIR * h);
        s.vx *= drag;
        s.vy *= drag;
        s.x += s.vx * h;
        s.y += s.vy * h;
        s.spin += s.av * h;
        if (s.x < BOMB.r) {
          s.x = BOMB.r;
          s.vx = Math.abs(s.vx) * BOUNCE;
        } else if (s.x > CANVAS.w - BOMB.r) {
          s.x = CANVAS.w - BOMB.r;
          s.vx = -Math.abs(s.vx) * BOUNCE;
        }
        if (s.y < BOMB.r) {
          s.y = BOMB.r;
          s.vy = Math.abs(s.vy) * BOUNCE;
        }
        // Docked, its lower half is *inside* the church — that is what makes it
        // a dome. Resolving that as a collision would fling it off the roof, so
        // an overlap that was already there when it was let go is ignored until
        // it has fallen clear: the dome comes loose and drops down the front.
        if (s.ghost) {
          if (s.y > CANVAS.h || !touching(s.x, s.y)) s.ghost = false;
        } else {
          contact();
        }
      }
    },
    [contact, touching],
  );

  /* ------------------------------------------------------------ paint ---- */
  const sync = useCallback(() => {
    const s = st.current;
    const sprite = spriteRef.current;
    if (sprite) {
      sprite.style.visibility = s.phase === 'blast' ? 'hidden' : '';
      sprite.classList.toggle('is-free', s.free);
      if (s.phase === 'docked') {
        // Back to being an ordinary layer. Leaving a translate(0,0) rotate(0)
        // on it would be invisible and still wrong: it is the church again.
        if (sprite.style.transform) sprite.style.transform = '';
      } else if (s.phase !== 'rising' && s.phase !== 'blast') {
        // While it is climbing back the transform belongs to the transition.
        sprite.style.transform =
          `translate(${(s.x - DOCK_X).toFixed(2)}px, ${(s.y - DOCK_Y).toFixed(2)}px)` +
          ` rotate(${s.spin.toFixed(4)}rad)`;
      }
    }
    const hit = hitRef.current;
    if (hit) {
      const grabbable = s.phase === 'docked' || s.phase === 'held' || s.phase === 'loose';
      hit.setAttribute('cx', s.x.toFixed(1));
      hit.setAttribute('cy', s.y.toFixed(1));
      hit.setAttribute('r', grabbable ? String(BOMB.r) : '0');
    }
  }, []);

  /** A filled disc, or an annulus when r0 > 0. Scanlines, so the edge is a hard
   *  pixel edge — arcs on a 2d context would be anti-aliased, and nothing else
   *  in this city has a soft edge. */
  const ink = useCallback(
    (data: Uint8ClampedArray, cx: number, cy: number, r0: number, r1: number,
     colour: [number, number, number], alpha: number) => {
      const [r, g, b] = colour;
      const a = Math.min(255, Math.max(0, Math.round(alpha * 255)));
      if (a === 0) return;
      const w = BLAST_R * 2;
      const top = Math.max(0, Math.ceil(cy - r1));
      const bot = Math.min(w - 1, Math.floor(cy + r1));
      for (let y = top; y <= bot; y++) {
        const dy = y - cy;
        const outer = Math.sqrt(Math.max(0, r1 * r1 - dy * dy));
        const inner = Math.abs(dy) < r0 ? Math.sqrt(r0 * r0 - dy * dy) : 0;
        const spans: [number, number][] = inner
          ? [[cx - outer, cx - inner], [cx + inner, cx + outer]]
          : [[cx - outer, cx + outer]];
        for (const [x0, x1] of spans) {
          const from = Math.max(0, Math.ceil(x0));
          const to = Math.min(w - 1, Math.floor(x1));
          for (let x = from; x <= to; x++) {
            const p = (y * w + x) * 4;
            data[p] = r;
            data[p + 1] = g;
            data[p + 2] = b;
            data[p + 3] = a;
          }
        }
      }
    },
    [],
  );

  const paintBlast = useCallback(
    (now: number, dt: number) => {
      const s = st.current;
      const canvas = blastRef.current;
      if (!canvas || !s.blastAt) return false;
      const ctx = canvas.getContext('2d');
      if (!ctx) return false;
      const age = now - s.blastAt;
      const w = BLAST_R * 2;
      if (!image.current) image.current = ctx.createImageData(w, w);
      const data = image.current.data;
      data.fill(0);

      if (age < BLAST_MS) {
        const t = age / BLAST_MS;
        // The ring runs out fast and slows: a shockwave, not a balloon.
        const ease = 1 - (1 - t) * (1 - t);
        const ring = 18 + (BLAST_R - 26) * ease;
        const thick = Math.max(2, 13 * (1 - t) + 2);
        ink(data, BLAST_R, BLAST_R, ring - thick, ring, HOT[2]!, 0.95 * (1 - t));
        ink(data, BLAST_R, BLAST_R, ring - thick * 0.45, ring, HOT[1]!, 0.9 * (1 - t));
        // The core collapses as the ring leaves.
        const core = BLAST_R * 0.46 * Math.pow(1 - t, 0.7);
        ink(data, BLAST_R, BLAST_R, 0, core, HOT[1]!, 1 - t * 0.6);
        ink(data, BLAST_R, BLAST_R, 0, core * 0.55, HOT[0]!, 1 - t * 0.4);
      }

      let alive = false;
      for (const p of bits.current) {
        if (p.life <= 0) continue;
        p.life -= dt;
        if (p.life <= 0) continue;
        alive = true;
        p.vy += GRAVITY * 0.55 * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        const fade = Math.min(1, p.life / 0.35);
        const px = Math.round(BLAST_R + p.x);
        const py = Math.round(BLAST_R + p.y);
        const colour = HOT[p.colour]!;
        const a = Math.round(fade * 255);
        for (let y = py; y < py + 2; y++) {
          if (y < 0 || y >= w) continue;
          for (let x = px; x < px + 2; x++) {
            if (x < 0 || x >= w) continue;
            const o = (y * w + x) * 4;
            data[o] = colour[0];
            data[o + 1] = colour[1];
            data[o + 2] = colour[2];
            data[o + 3] = a;
          }
        }
      }
      ctx.putImageData(image.current, 0, 0);
      const busy = age < BLAST_MS || alive;
      if (!busy) s.blastAt = 0;
      return busy;
    },
    [ink],
  );

  const shake = useCallback(
    (now: number) => {
      const s = st.current;
      const el = shakeRef.current;
      if (!el) return false;
      const left = s.shakeAt - now;
      if (left <= 0) {
        if (s.shakeAt) {
          s.shakeAt = 0;
          el.style.transform = '';
        }
        return false;
      }
      const k = (left / SHAKE_MS) * SHAKE_PX;
      el.style.transform =
        `translate(${(Math.random() * 2 - 1) * k}px, ${(Math.random() * 2 - 1) * k}px)`;
      return true;
    },
    [shakeRef],
  );

  /* ------------------------------------------------------------ frames --- */
  const later = useCallback((ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);

  const rise = useCallback(() => {
    const s = st.current;
    s.phase = 'rising';
    s.x = DOCK_X;
    s.y = DOCK_Y;
    s.vx = 0;
    s.vy = 0;
    s.av = 0;
    s.spin = 0;
    s.free = false;
    s.asleep = false;
    s.armed = false;
    s.fuseAt = 0;
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const sprite = spriteRef.current;
    if (sprite) {
      sprite.classList.remove('is-free');
      sprite.style.visibility = '';
      sprite.style.transform = `translateY(${BOMB.hide}px)`;
      if (!calm) {
        // The forced reflow is load-bearing, same as Kulčina's tower: without a
        // style pass between the two writes the browser coalesces them and the
        // new dome simply appears, already docked.
        void sprite.offsetWidth;
        sprite.classList.add('is-rising');
        sprite.style.transform = 'translateY(0px)';
      }
    }
    later(calm ? 0 : RISE_MS, () => {
      st.current.phase = 'docked';
      const el = spriteRef.current;
      if (el) {
        el.classList.remove('is-rising');
        el.style.transform = '';
      }
      sync();
    });
  }, [later, sync]);

  const explode = useCallback(
    (now: number) => {
      const s = st.current;
      s.phase = 'blast';
      s.armed = false;
      s.blastAt = now;
      s.bx = s.x;
      s.by = s.y;
      s.pointer = -1;
      const canvas = blastRef.current;
      if (canvas) {
        canvas.style.left = `${Math.round(s.bx) - BLAST_R}px`;
        canvas.style.top = `${Math.round(s.by) - BLAST_R}px`;
        canvas.style.visibility = '';
      }
      bits.current = Array.from({ length: DEBRIS }, () => {
        const a = Math.random() * Math.PI * 2;
        const v = 120 + Math.random() * 400;
        return {
          x: 0,
          y: 0,
          vx: Math.cos(a) * v,
          vy: Math.sin(a) * v,
          life: (DEBRIS_MS / 1000) * (0.55 + Math.random() * 0.45),
          colour: Math.floor(Math.random() * HOT.length),
        };
      });
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        s.shakeAt = now + SHAKE_MS;
      }
      later(RISE_AT_MS, rise);
    },
    [later, rise],
  );

  const tick = useCallback(() => {
    const s = st.current;
    const now = performance.now();
    const dt = clock.current ? (now - clock.current) / 1000 : 1 / 60;
    clock.current = now;

    if (s.phase === 'loose') integrate(dt);
    if (s.fuseAt && now >= s.fuseAt && (s.phase === 'held' || s.phase === 'loose')) {
      explode(now);
    }
    const flaring = paintBlast(now, Math.min(dt, 0.05));
    const shaking = shake(now);
    sync();

    // Docked, or lying still in a dark city with nothing due: there is nothing
    // left to compute, and an inert grenade could sit there for minutes.
    const idle = s.phase === 'docked' || (s.phase === 'loose' && s.asleep && !s.fuseAt);
    const busy = !idle || flaring || shaking;
    raf.current = busy ? requestAnimationFrame(tick) : 0;
    if (!busy) clock.current = 0;
  }, [explode, integrate, paintBlast, shake, sync]);

  const start = useCallback(() => {
    if (!raf.current) {
      clock.current = 0;
      raf.current = requestAnimationFrame(tick);
    }
  }, [tick]);

  /** Light it: the bang is `ms` away, and the clip is scheduled to land on it. */
  const lightFuse = useCallback(
    (ms: number) => {
      st.current.fuseAt = performance.now() + ms;
      fireGrenade(st.current.fuseAt);
      start();
    },
    [start],
  );

  /**
   * The church's lights are the fuse's power.
   *
   * Dark, an armed grenade is inert — it still flies and bounces, it simply
   * never goes off, and any choir already singing is cut. Lit again, the choir
   * starts at that instant and the bang follows it out of the clip.
   */
  useEffect(() => {
    const was = litRef.current;
    litRef.current = lit;
    if (was === lit) return; // the first run, and StrictMode's second one
    const s = st.current;
    if (!s.armed || s.phase === 'blast' || s.phase === 'rising') return;
    if (lit) {
      if (!s.fuseAt) lightFuse(RELIGHT_MS);
    } else if (s.fuseAt) {
      s.fuseAt = 0;
      stopGrenade();
    }
  }, [lit, lightFuse]);

  useEffect(
    () => () => {
      cancelAnimationFrame(raf.current);
      raf.current = 0;
      for (const t of timers.current) clearTimeout(t);
      timers.current = [];
    },
    [],
  );

  /* ---------------------------------------------------------- pointer ---- */
  /** Pointer -> canvas coordinates, through the hit SVG's own viewBox. */
  const toCanvas = useCallback((e: { clientX: number; clientY: number }) => {
    const svg = hitRef.current?.ownerSVGElement;
    if (!svg) return null;
    const box = svg.getBoundingClientRect();
    if (!box.width || !box.height) return null;
    const vb = svg.viewBox.baseVal;
    return {
      x: ((e.clientX - box.left) / box.width) * vb.width + vb.x,
      y: ((e.clientY - box.top) / box.height) * vb.height + vb.y,
    };
  }, []);

  const onPointerDown = useCallback<PointerEventHandler<SVGGElement>>(
    (e) => {
      const s = st.current;
      if (s.phase === 'blast' || s.phase === 'rising' || s.pointer >= 0) return;
      const p = toCanvas(e);
      if (!p) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      s.pointer = e.pointerId;
      s.phase = 'held';
      s.asleep = false;
      s.vx = 0;
      s.vy = 0;
      s.av = 0;
      // Grab it where it was grabbed, so it does not jump to the finger.
      s.grabX = s.x - p.x;
      s.grabY = s.y - p.y;
      trail.current = [{ t: performance.now(), x: p.x, y: p.y }];
      armGrenade();
      sync();
      start();
    },
    [start, sync, toCanvas],
  );

  const onPointerMove = useCallback<PointerEventHandler<SVGGElement>>(
    (e) => {
      const s = st.current;
      if (s.phase !== 'held' || e.pointerId !== s.pointer) return;
      const p = toCanvas(e);
      if (!p) return;
      // A captured pointer goes anywhere, including under the page. The band is
      // the world, so the hand stops at its edges.
      s.x = Math.min(CANVAS.w - BOMB.r, Math.max(BOMB.r, p.x + s.grabX));
      s.y = Math.min(CANVAS.h - BOMB.r, Math.max(BOMB.r, p.y + s.grabY));
      // Lifted in front of the city only once it is out of the church, and
      // then for good. Promoting any earlier covers the church's own
      // crenellations and lit arches with the ball, which reads as the dome
      // floating in front of the roof instead of being pulled out of it.
      if (!s.free && clearOfSocket(s.x, s.y)) s.free = true;
      const now = performance.now();
      trail.current.push({ t: now, x: p.x, y: p.y });
      while (trail.current.length > 2 && now - (trail.current[0]?.t ?? now) > THROW_WINDOW_MS * 2) {
        trail.current.shift();
      }
      sync();
    },
    [sync, toCanvas],
  );

  const release = useCallback<PointerEventHandler<SVGGElement>>(
    (e) => {
      const s = st.current;
      if (s.phase !== 'held' || e.pointerId !== s.pointer) return;
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      s.pointer = -1;
      s.phase = 'loose';
      // Let go, it is in front whether or not it ever cleared the church: a
      // bare tap drops it down the *front* of the building, and anything that
      // came free and is falling has to be visible while it falls.
      s.free = true;
      s.asleep = false;
      s.ghost = touching(s.x, s.y);

      const now = performance.now();
      const path = trail.current;
      const last = path[path.length - 1];
      const from = path.find((q) => now - q.t <= THROW_WINDOW_MS) ?? path[0];
      if (last && from && last !== from) {
        const dt = (last.t - from.t) / 1000;
        if (dt > 0.008) {
          let vx = (last.x - from.x) / dt;
          let vy = (last.y - from.y) / dt;
          const speed = Math.hypot(vx, vy);
          if (speed > MAX_THROW) {
            vx *= MAX_THROW / speed;
            vy *= MAX_THROW / speed;
          }
          s.vx = vx;
          s.vy = vy;
          s.av = vx / (BOMB.r * TUMBLE);
        }
      }
      trail.current = [];
      // The pin comes out on the FIRST release and never goes back in, so
      // catching it again buys you nothing — which is the whole joke. Whether
      // it is actually *burning* is the church's business.
      if (!s.armed) {
        s.armed = true;
        if (litRef.current) lightFuse(FUSE_MS);
      }
      start();
    },
    [lightFuse, start, touching],
  );

  return {
    spriteRef,
    blastRef,
    hitRef,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: release,
      onPointerCancel: release,
    },
  };
}

/** For the harness: the dimensions the rest of the app agrees on. */
export const GRENADE = { DOCK_X, DOCK_Y, BLAST_R, RISE_AT_MS, RISE_MS, BLAST_MS } as const;
